import {
  LayoutDashboard,
  Users,
  Package,
  CalendarPlus,
  Receipt,
  Tent,
  PhoneCall,
  LogOut,
  ChevronRight,
  X,
} from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import logo from "../assets/logo.png";

function Sidebar({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    onClose?.();
    setConfirmLogout(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("resetUserId");
    localStorage.removeItem("email");
    navigate("/");
  };

  const mainMenus = [
    { name: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { name: "Bookings", icon: CalendarPlus, path: "/bookings" },
    { name: "Customers", icon: Users, path: "/customer" },
    { name: "Camp", icon: Tent, path: "/camp" },
    { name: "Follow Up", icon: PhoneCall, path: "/followups" },
    { name: "Stock", icon: Package, path: "/stock" },
    { name: "Invoice", icon: Receipt, path: "/invoice" },
  ];

  const userName = localStorage.getItem("userName") || "Admin User";
  const userEmail = localStorage.getItem("email") || "admin@zaoptical.com";
  const initials = userName
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm transition-opacity lg:hidden
          ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-hidden="true"
      />
      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 z-50 flex h-[100dvh] w-[280px] sm:w-72 flex-col border-r border-slate-200 bg-white shadow-xl lg:shadow-none
          transition-transform duration-300 ease-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 sm:px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl overflow-hidden shadow-md shadow-blue-500/20">
              <img src={logo} alt="ZA Optical" className="h-full w-full object-cover" />
            </div>
            <div className="leading-tight">
              <p className="text-[15px] font-semibold tracking-tight text-slate-900">
                ZA Optical
              </p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-0.5">
                Optical CRM
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden h-9 w-9 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5">
          <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            Main Menu
          </p>
          <div className="space-y-1">
            {mainMenus.map((menu) => {
              const Icon = menu.icon;
              return (
                <NavLink
                  key={menu.path}
                  to={menu.path}
                  data-testid={`nav-${menu.name.toLowerCase()}`}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all
                    ${
                      isActive
                        ? "bg-blue-50 text-blue-700"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-blue-600 transition-opacity ${
                          isActive ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <Icon
                        size={19}
                        className={
                          isActive
                            ? "text-blue-600"
                            : "text-slate-400 group-hover:text-slate-700"
                        }
                        strokeWidth={isActive ? 2.4 : 2}
                      />
                      <span className="flex-1">{menu.name}</span>
                      <ChevronRight
                        size={14}
                        className={`transition-all ${
                          isActive
                            ? "opacity-100 text-blue-600 translate-x-0"
                            : "opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-3 rounded-lg px-2.5 py-2.5 mb-2">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-600 to-sky-500 grid place-items-center text-white text-xs font-bold shadow-sm shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {userName}
              </p>
              <p className="text-[11px] text-slate-500 truncate">{userEmail}</p>
            </div>
          </div>

          {!confirmLogout ? (
            <button
              data-testid="logout-btn"
              onClick={() => setConfirmLogout(true)}
              className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-red-50 hover:text-red-600"
            >
              <LogOut
                size={19}
                className="text-slate-400 group-hover:text-red-500"
                strokeWidth={2}
              />
              Logout
            </button>
          ) : (
            <div className="rounded-lg border border-red-200 bg-red-50/60 p-2.5">
              <p className="text-xs text-red-700 mb-2 px-1">
                Are you sure you want to log out?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={logout}
                  className="flex-1 rounded-md bg-red-600 hover:bg-red-700 py-1.5 text-xs font-medium text-white transition"
                >
                  Yes, Logout
                </button>
                <button
                  onClick={() => setConfirmLogout(false)}
                  className="flex-1 rounded-md bg-white border border-slate-200 hover:bg-slate-50 py-1.5 text-xs font-medium text-slate-700 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <p className="mt-2 text-center text-[10px] text-slate-400 tracking-wide">
            v1.0.0 · © 2026 ZA Optical
          </p>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
